import type { GetRequestsListResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import apiClient from "@/lib/api/client";

interface UseTeamLeadTeamRequestsParams {
  designerIds?: number[];
  direction?: "asc" | "desc";
  enabled?: boolean;
  search?: string;
  sortBy?: string;
  statusIds?: number[];
  userId?: string;
}

const TEAM_REQUESTS_LIMIT = 1000;

/**
 * Hook for fetching Team Lead "Team Requests" data
 * Shows requests assigned to team designers (excludes team lead, excludes unassigned)
 * Supports multi-select filters for designers and statuses
 */
export function useTeamLeadTeamRequests({
  designerIds = [],
  direction = "desc",
  enabled = true,
  search = "",
  sortBy = "createdAt",
  statusIds = [],
  userId,
}: UseTeamLeadTeamRequestsParams) {
  return useQuery({
    enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        direction,
        limit: TEAM_REQUESTS_LIMIT.toString(),
        page: "1",
        sortBy,
        tab: "team-requests",
        ...(search && { search }),
        ...(userId && { forUserId: userId }),
      });

      // Add designer IDs if selected (as repeated query parameters)
      if (designerIds.length > 0) {
        for (const id of designerIds) {
          params.append("designerIds", id.toString());
        }
      }

      // Add status IDs if selected (as repeated query parameters)
      if (statusIds.length > 0) {
        for (const id of statusIds) {
          params.append("statusIds", id.toString());
        }
      }

      const response = await apiClient
        .url(`/requests?${params.toString()}`)
        .get()
        .json<GetRequestsListResponse>();

      return {
        ...response,
        truncated: (response.total ?? 0) > response.requests.length,
      };
    },
    queryKey: [
      "teamLeadTeamRequests",
      sortBy,
      direction,
      search,
      designerIds,
      statusIds,
      ...(userId ? [userId] : []),
    ],
    staleTime: 30_000, // 30 seconds
  });
}
